import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Grid, Typography, Paper, TextField } from '@mui/material';
import CommonOperationScreen from '../../components/CommonOperationScreen/CommonOperationScreen';
import { StyledTable, ScanOrEnterBarcodeForm, JlrButton } from '../../components';
import TableActionBar, { GradientButton } from '../../components/TableActionBar/TableActionBar';
import { useResolutionScale } from '../../hooks';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import { fetchAllMrVehicles, fetchMrVehicleHistory, receiveMrPart } from '../../redux/actionCreators/majorRepair';
import {
	DARK_MODE,
	END_OF_DAY_TIME,
	MAJOR_REPAIR_MESSAGES,
	OPERATIONS,
	REDIRECT_URLS,
	START_OF_DAY_TIME,
} from '../../constants';
import darkBarcode from '../../assets/images/darkbarcode.png';
import { formatDateTimeWithOffset, getLocalDateString } from '../../utils/utils';

// Keep the active MR queue table unchanged.
const mrVehicleColumns = [
	{
		width: 180,
		title: 'ASN',
		field: 'asn',
		align: 'center',
		render: (row) => row?.asn ?? '—',
	},
	{
		width: 260,
		title: 'SERIAL NUMBER (VIN)',
		field: 'serialNo',
		align: 'center',
		render: (row) => row?.serialNo ?? '—',
	},
	{
		width: 200,
		title: 'MODEL',
		field: 'model',
		align: 'center',
		render: (row) => row?.model ?? '—',
	},
	{
		width: 220,
		title: 'KSPEC',
		field: 'kspec',
		align: 'center',
		render: (row) => row?.kspec ?? '—',
	},
	{
		width: 180,
		title: 'LOT NO',
		field: 'lotNo',
		align: 'center',
		render: (row) => row?.lotNo ?? '—',
	},
	{
		width: 240,
		title: 'ORIGIN STATION',
		field: 'lineTakeOutStationDescription',
		align: 'center',
		render: (row) => row?.lineTakeOutStationDescription || (row?.lineTakeOutStationId ? `Station ${row.lineTakeOutStationId}` : '—'),
	},
	];

const processedVehicleColumns = [
	{
		width: 180,
		title: 'ASN',
		field: 'asn',
		align: 'center',
		render: (row) => row?.asn ?? '—',
	},
	{
		width: 260,
		title: 'SERIAL NUMBER (VIN)',
		field: 'serialNo',
		align: 'center',
		render: (row) => row?.serialNo ?? '—',
	},
	{
		width: 200,
		title: 'MODEL',
		field: 'model',
		align: 'center',
		render: (row) => row?.model ?? '—',
	},
	{
		width: 220,
		title: 'KSPEC',
		field: 'kspec',
		align: 'center',
		render: (row) => row?.kspec ?? '—',
	},
	{
		width: 180,
		title: 'LOT NO',
		field: 'lotNo',
		align: 'center',
		render: (row) => row?.lotNo ?? '—',
	},
	{
		width: 180,
		title: 'Origin Station',
		field: 'lineTakeOutStationDescription',
		align: 'center',
		render: (row) => row?.lineTakeOutStationDescription || '—',
	},
	{
		width: 180,
		title: 'Destination Station',
		field: 'lineTakeInStationDescription',
		align: 'center',
		render: (row) => row?.lineTakeInStationDescription || '—',
	},
	{
		width: 220,
		title: 'LINE TAKE-IN USER',
		field: 'lineTakeInUser',
		align: 'center',
		render: (row) => row?.lineTakeInUser || '—',
	},
	{
		width: 220,
		title: 'LINE TAKE-OUT USER',
		field: 'lineTakeOutUser',
		align: 'center',
		render: (row) => row?.lineTakeOutUser || '—',
	},
];

const mapStateToProps = (state) => ({
	currentLineItem: state.station.currentLineItem,
	receivePart: state.receivePart,
	themeMode: state.theme.themeMode,
});

const MajorRepair = ({ currentLineItem, receivePart, themeMode, view = 'station' }) => {
	const dispatch = useDispatch();
	const { scaleValue } = useResolutionScale();
	const { showMessageOverlay } = useMessageContextManager();
	const navigate = useNavigate();
	const location = useLocation();

	const [mrVehicles, setMrVehicles] = useState([]);
	const [processedVehicles, setProcessedVehicles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [processedLoading, setProcessedLoading] = useState(false);
	const [tableColumns, settableColumns] = useState(mrVehicleColumns);
	const [tableFilters,settableFilters] = useState({});
	const [processedTableColumns, setProcessedTableColumns] = useState(processedVehicleColumns);
	const [processedTableFilters, setProcessedTableFilters] = useState({});
	const [processedFilterResetKey, setProcessedFilterResetKey] = useState(0);
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const processedRequestId = useRef(0);
	const filteredProcessedVehicles = useMemo(() => {
		if (!processedTableFilters || Object.keys(processedTableFilters).length === 0) {
			return processedVehicles;
		}

		return processedVehicles.filter((row) =>
			Object.entries(processedTableFilters).every(([field, selectedValues]) => {
				if (!selectedValues?.length) return true;
				return selectedValues.some((value) => String(value) === String(row?.[field]));
			}),
		);
	}, [processedVehicles, processedTableFilters]);

	const activeVin = currentLineItem?.serialNumber || receivePart?.serialNumber;

	const loadMrVehicles = useCallback(() => {
		setLoading(true);
		dispatch(
			fetchAllMrVehicles(
				(data) => {
					setLoading(false);
					const list = Array.isArray(data) ? data : [];
					setMrVehicles(list.map((item, idx) => ({ ...item, id: item.asn || idx })));
				},
				() => {
					setLoading(false);
					setMrVehicles([]);
				},
			),
		);
		}, [dispatch]);

	const loadProcessedVehicles = useCallback((rangeStart = '', rangeEnd = '') => {
		const requestId = ++processedRequestId.current;
		setProcessedLoading(true);
		setProcessedVehicles([]);
		dispatch(
			fetchMrVehicleHistory(
				rangeStart,
				rangeEnd,
			(data) => {
					if (requestId !== processedRequestId.current) return;
					setProcessedLoading(false);
					const list = Array.isArray(data) ? data : [];
					setProcessedVehicles(list.map((item, idx) => ({ ...item, id: item.asn || idx })));
				},
				() => {
					if (requestId !== processedRequestId.current) return;
					setProcessedLoading(false);
					setProcessedVehicles([]);
				},
			),
		);
	}, [dispatch]);

	useEffect(() => {
		loadMrVehicles();
			loadProcessedVehicles();
	}, [loadMrVehicles, loadProcessedVehicles, activeVin]);

	const handleHistorySearch = () => {
		if (!startDate || !endDate) {
			showMessageOverlay({ contextText: 'Select both start and end dates', error: true });
			return;
		}
		const startDateValue = new Date(`${startDate}${START_OF_DAY_TIME}`);
		const endDateValue = new Date(`${endDate}${END_OF_DAY_TIME}`);
		if (endDate > getLocalDateString()) {
			showMessageOverlay({ contextText: 'Invalid end date', info: 'End date cannot be later than today.', error: true });
			return;
		}
		if (Number.isNaN(startDateValue.getTime()) || Number.isNaN(endDateValue.getTime()) || endDateValue <= startDateValue) {
			showMessageOverlay({ contextText: 'Invalid date range', info: 'End date must not be before the start date.', error: true });
			return;
		}
		const startDateTime = formatDateTimeWithOffset(`${startDate}${START_OF_DAY_TIME}`);
		const endDateTime = formatDateTimeWithOffset(`${endDate}${END_OF_DAY_TIME}`);
		loadProcessedVehicles(startDateTime, endDateTime);
	};

	const handleDefaultHistory = () => {
		setStartDate('');
		setEndDate('');
		setProcessedTableFilters({});
		setProcessedFilterResetKey((key) => key + 1);
		loadProcessedVehicles();
	};

	useEffect(() => {
		const majorRepairRoute = REDIRECT_URLS[OPERATIONS.MAJOR_REPAIR];
		if (activeVin && location.pathname !== majorRepairRoute) {
			navigate(majorRepairRoute, { replace: true });
		}
	}, [activeVin, location.pathname, navigate]);

	const handleScanVin = (scannedValue, resetBarcodeValue) => {
		const vin = scannedValue.substring(0, 17);

		const closeOverlay = showMessageOverlay({
			contextText: 'Receiving vehicle at Major Repair...',
			loading: true,
		});

		dispatch(
			receiveMrPart(
				vin,
				() => {
					closeOverlay();
				},
				(err) => {
					closeOverlay();
					if (typeof resetBarcodeValue === 'function') resetBarcodeValue();
					showMessageOverlay({
						contextText: MAJOR_REPAIR_MESSAGES.RECEIVE_ERROR,
						info: err?.response?.data?.message || MAJOR_REPAIR_MESSAGES.VEHICLE_NOT_IN_MR,
						error: true,
					});
				},
			),
		);
	};

	// If there is an active scanned VIN/item, render the Major Repair station view with "MOVE OUT" button
	if (activeVin && view === 'station') {
		return <CommonOperationScreen operationName="MAJOR_REPAIR" buttonLabel="MOVE OUT" />;
	}

	// WAHA / Transfer Drop style view listing all vehicles in Major Repair
	return (
		<Grid container sx={{ p: 2 }}>
			<Grid item xs={12}>
					<Grid container direction="column" spacing={2}>
						{view === 'station' && (
							<>
						<Grid item xs={12}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
							<Typography variant="h5" sx={{ fontWeight: 600, fontSize: `${scaleValue(24)}px` }}>
								Vehicles For Repair ({mrVehicles.length})
							</Typography>
							<JlrButton variant="outlined" size="small" onClick={loadMrVehicles}>
								Refresh List
							</JlrButton>
						</Box>

						<TableActionBar
							showColumns
							showFilter
								rows={mrVehicles}
								columns={tableColumns}
								defaultColumns={mrVehicleColumns}
								onColumnChange={settableColumns}
								onFilterChange={settableFilters}
							/>
						<StyledTable
							rows={mrVehicles}
							columns={tableColumns}
							filters={tableFilters}
							showCheckBox={false}
							elevated
							isRowClickCopy
						/>
						{!loading && mrVehicles.length === 0 && (
							<Typography sx={{ mt: 2, textAlign: 'center' }}>No vehicles currently in Major Repair.</Typography>
						)}
					</Grid>

					<Grid item xs={12} sx={{ mt: 2 }}>
						<Paper
							elevation={3}
							sx={{
								p: 3,
								background: themeMode === DARK_MODE ? '#1E2227' : '#FFFFFF',
								borderRadius: '16px',
							}}
						>
							<Grid container alignItems="center" spacing={2} justifyContent="center">
								<Grid item display="flex" alignItems="center">
									<img src={darkBarcode} alt="scan barcode" style={{ height: 40, width: 40 }} />
								</Grid>
								<Grid item xs={10} md={9} lg={8}>
									<ScanOrEnterBarcodeForm
										enableAutoEnter
										isAlphaNumericValidation={false}
										onSubmit={handleScanVin}
										nameOfComponent="MajorRepair"
									/>
								</Grid>
							</Grid>
						</Paper>
						</Grid>
							</>
						)}

					{view === 'processed' && <Grid item xs={12}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
							<Typography variant="h5" sx={{ fontWeight: 600, fontSize: `${scaleValue(24)}px` }}>
								Processed Vehicles ({processedVehicles.length})
							</Typography>
						</Box>

						<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
							<TextField
								label="Start date"
								type="date"
								size="small"
								value={startDate}
								onChange={(event) => setStartDate(event.target.value)}
								inputProps={{ max: getLocalDateString() }}
								InputLabelProps={{ shrink: true }}
							/>
							<TextField
								label="End date"
								type="date"
								size="small"
								value={endDate}
								onChange={(event) => setEndDate(event.target.value)}
								inputProps={{ max: getLocalDateString() }}
								InputLabelProps={{ shrink: true }}
							/>
							<GradientButton
								gradient="linear-gradient(90deg, #0077B6 0%, #00B4D8 100%)"
								onClick={handleHistorySearch}
								sx={{ minWidth: `${scaleValue(160)}px`, height: `${scaleValue(54)}px` }}
							>
								Search History
							</GradientButton>
							<GradientButton
								gradient="linear-gradient(90deg, #7B2CBF 0%, #C77DFF 100%)"
								onClick={handleDefaultHistory}
								sx={{ minWidth: `${scaleValue(50)}px`, height: `${scaleValue(54)}px`, px: 2 }}
							>
								Last 24 hours
							</GradientButton>

						</Box>

						<TableActionBar
							key={processedFilterResetKey}
							showColumns
							showFilter
							rows={processedVehicles}
							columns={processedTableColumns}
							defaultColumns={processedVehicleColumns}
							onColumnChange={setProcessedTableColumns}
							onFilterChange={setProcessedTableFilters}
						/>
						<StyledTable
							key={`processed-${JSON.stringify(processedTableFilters)}-${filteredProcessedVehicles.length}`}
							rows={filteredProcessedVehicles}
							columns={processedTableColumns}
							filters={{}}
							showCheckBox={false}
							elevated
							isRowClickCopy
						/>
						{!processedLoading && processedVehicles.length === 0 && (
							<Typography sx={{ mt: 2, textAlign: 'center' }}>No processed vehicle history found.</Typography>
						)}
					</Grid>}
				</Grid>
			</Grid>
		</Grid>
	);
};

export default connect(mapStateToProps)(MajorRepair);
